<template>
  <div class="booking-wizard" :data-step="currentindex">
    <div class="qb-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="progress">
      <span class="qb-progress-bar" :style="{ width: `${progress}%` }"></span>
    </div>
    <!-- Which salon this booking is for (035). A link from the website skips the salon
         step, so without this the salon first showed up after the booking was made. -->
    <div v-if="salon && currentindex >= 2 && currentindex <= 6" class="qb-salon">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg>
      <span class="qb-salon-txt">
        <small>{{ $t('quick_booking.lbl_booking_at') }}</small>
        <b>{{ salon.name }}</b>
        <span v-if="salon.address">{{ salon.address }}</span>
      </span>
      <!-- Only on the services step: that is one step from the salon list, so nothing
           chosen later (specialist, time) is left behind for the old salon. -->
      <button v-if="currentindex === 2" type="button" class="qb-salon-change" @click="nextTabChange(1)">{{ $t('quick_booking.lbl_change_salon') }}</button>
    </div>
    <div class="container-fluid p-0">
      <div class="widget-layout">
        <div class="widget-pannel">
          <div id="wizard-tab" class="iq-card iq-card-sm tab-content">
            <template v-for="(item, index) in setupArray" :key="`panel-${index}`">
              <div :id="item.type" :class="`iq-fade iq-tab-pannel ${activeCheck(item.id)}`">
                <TabPanel :type="item.type" :title="item.title" :detail="item.detail" :show-head="item.show_head !== false" :wizard-next="item.next" :wizard-prev="item.prev" @onClick="nextTabChange" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import TabPanel from './TabPanel.vue'
import { useQuickBooking } from '../store/quick-booking'

// Setup Array
const setupArray = reactive([

  {
    id: 1,
    title: 'quick_booking.lbl_select_branch',
    type: 'select-branch',
    is_vissible: true,
    // A wall of salon photos needs no caption, and on a phone the heading was
    // pushing the first card below the fold.
    show_head: false,
    detail: 'quick_booking.lbl_brach_description',
    done: false,
    next: 2,
    prev: null
  },
  {
    id: 2,
    title: 'quick_booking.lbl_select_service',
    type: 'select-service',
    is_vissible: true,
    // Same reasoning: the category strip and the search box already frame the
    // step, so the heading is spent screen on a phone.
    show_head: false,
    detail: 'quick_booking.lbl_service_description',
    done: false,
    next: 3,
    prev: 1
  },
  {
    id: 3,
    title: 'quick_booking.lbl_select_select_staff',
    type: 'select-employee',
    is_vissible: true,
    detail: 'quick_booking.lbl_staff_description',
    done: false,
    next: 4,
    prev: 2
  },
  {
    id: 4,
    title: 'quick_booking.lbl_select_date_time',
    type: 'select-date-time',
    is_vissible: true,
    detail: 'quick_booking.lbl_sate_time_description',
    done: false,
    next: 5,
    prev: 3
  },
  {
    id: 5,
    title: 'quick_booking.lbl_customer_detail',
    type: 'customer-details',
    is_vissible: true,
    detail: 'quick_booking.lbl_customer_description',
    done: false,
    next: 6,
    prev: 4
  },
  {
    id: 6,
    title: 'quick_booking.lbl_confrimation',
    type: 'select-confirm',
    is_vissible: true,
    detail: 'quick_booking.lbl_confrim_msg',
    done: false,
    next: 7,
    prev: 5
  },
  {
    id: 7,
    title: 'quick_booking.lbl_confrimation_detail',
    type: 'confirmation-detail',
    is_vissible: false,
    detail: 'quick_booking.lbl_confrim_description',
    done: false,
    next: null,
    prev: null
  },
  {
    id: 8,
    title: 'quick_booking.lbl_error',
    type: 'error',
    is_vissible: false,
    detail: 'quick_booking.lbl_error_description',
    done: false,
    next: null,
    prev: 5
  },
])
const currentindex = ref(1)
const store = useQuickBooking()
const branches = (typeof window !== 'undefined' && window.QB_BRANCHES) || {}
const salon = computed(() => branches[store.booking.branch_id] || null)
const activeCheck = (value) => (currentindex.value == value ? 'active' : '')
const doneCheck = (value) => (currentindex.value > value ? true : false)
const nextTabChange = (val) => {
  currentindex.value = val
  scrollToTop()
}

/* With the step rail gone this line is the only thing left that says how far
   through the booking you are. Only the six visible steps are counted:
   `confirmation-detail` is the end state and `error` is not part of the
   sequence, so it collapses the bar to nothing rather than showing a position. */
const visibleSteps = setupArray.filter((item) => item.is_vissible)
const progress = computed(() => {
  const position = visibleSteps.findIndex((item) => item.id === currentindex.value)
  if (position >= 0) return Math.round(((position + 1) / visibleSteps.length) * 100)
  return currentindex.value === 8 ? 0 : 100
})

/* The brand block lives in the blade layout, outside this component, so the
   current step is published on <html> for the stylesheet to key off. That is
   what lets the date & time step drop the logo on a phone. */
const publishStep = (value) => {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-qb-step', String(value))
}
onMounted(() => {
  publishStep(currentindex.value)
  // Booking links from the public website (030) come with the salon already chosen
  // (?branch=…, checked server-side into window.QB_PRESELECT): start on the services step.
  const pre = typeof window !== 'undefined' ? window.QB_PRESELECT : null
  if (pre && pre.branch_id) {
    store.updateBookingValues({ key: 'branch_id', value: String(pre.branch_id) })
    currentindex.value = 2
  }
})
watch(currentindex, (value) => publishStep(value))

// On small screens bring the panel back into view on every step change
const scrollToTop = () => {
  if (typeof window === 'undefined' || window.innerWidth > 1024) return
  window.requestAnimationFrame(() => {
    const wizard = document.querySelector('.booking-wizard')
    if (wizard) wizard.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}
</script>
